import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {devtools, persist, createJSONStorage} from 'zustand/middleware';
import {
  DocumentChoice,
  IMAGE_SERVICE_ORIGIN,
  ImageDetails,
  IV_DOCUMENT_IMAGE_TYPES,
  LivenessAction,
} from '@chekin/core';

export const DEFAULT_IMAGE_DETAILS = {
  [IV_DOCUMENT_IMAGE_TYPES.front]: {
    id: '',
    image: null,
    uploaded: false,
    mrzDetected: false,
    faceDetected: false,
    type: IV_DOCUMENT_IMAGE_TYPES.front,
  },
  [IV_DOCUMENT_IMAGE_TYPES.back]: {
    id: '',
    image: null,
    uploaded: false,
    mrzDetected: false,
    faceDetected: false,
    service: IMAGE_SERVICE_ORIGIN.ocr,
    type: IV_DOCUMENT_IMAGE_TYPES.back,
  },
  [IV_DOCUMENT_IMAGE_TYPES.selfie]: {
    id: '',
    image: null,
    uploaded: false,
    faceDetected: false,
    service: IMAGE_SERVICE_ORIGIN.biomatch,
    type: IV_DOCUMENT_IMAGE_TYPES.selfie,
  },
};

export type IVStoreState = {
  isRemoteSession: boolean;
  setupData: {
    documentType?: DocumentChoice;
    nationality?: string;
  };
  imagesDetails: {
    [key in IV_DOCUMENT_IMAGE_TYPES]: Partial<ImageDetails> & {
      type: IV_DOCUMENT_IMAGE_TYPES;
    };
  };
  ocrResultData: {
    ocrPassed: boolean;
  };
  biomatchResultData: {
    biomatchPassed: boolean;
    distance: number | null;
  };
  livenessResultData?: {
    passed?: boolean;
    completedActions?: LivenessAction[];
    livenessVideoUrl?: string;
    performedBy?: 'CLIENT' | 'SERVER';
  };
  isIvFailed: boolean;
  isPropertyQRScanned: boolean;
};

export const initialState: IVStoreState = {
  isRemoteSession: false,
  setupData: {},
  imagesDetails: DEFAULT_IMAGE_DETAILS,
  ocrResultData: {
    ocrPassed: false,
  },
  biomatchResultData: {
    biomatchPassed: false,
    distance: 0,
  },
  isIvFailed: false,
  isPropertyQRScanned: false,
};

type Actions = {
  setDocumentType: (documentType: DocumentChoice) => void;
  setNationality: (nationality: string) => void;
  setOCRDocumentPassed: (ocrPassed: boolean) => void;
  setBiomatchPassed: (biomatchPassed: boolean) => void;
  setDistanceBiomatchComparing: (distance: number) => void;
  setImageDetails: (
    payload: Partial<ImageDetails> & {type: IV_DOCUMENT_IMAGE_TYPES},
  ) => void;
  resetIVConfigState: () => void;
  resetIVSetupFormDate: () => void;
  resetAllIVState: () => void;
  initIVState: () => void;
  sync: (identityVerificationSchema: Partial<IVStoreState>) => void;
  setRemoteSession: (flag: boolean) => void;
  setLivenessResultData: (
    livenessResultData: Partial<IVStoreState['livenessResultData']>,
  ) => void;
  setIsIvFailed: (isIvFailed: boolean) => void;
  setPropertyQRScanned: (isPropertyQRScanned: boolean) => void;
};

export const useIVStore = create<IVStoreState & Actions>()(
  devtools(
    persist(
      immer(set => ({
        ...initialState,
        setDocumentType: documentType =>
          set(state => {
            state.setupData.documentType = documentType;
          }),
        setNationality: nationality =>
          set(state => {
            state.setupData.nationality = nationality;
          }),
        setOCRDocumentPassed: ocrPassed =>
          set(state => {
            state.ocrResultData.ocrPassed = ocrPassed;
          }),
        setBiomatchPassed: biomatchPassed =>
          set(state => {
            state.biomatchResultData.biomatchPassed = biomatchPassed;
          }),
        setDistanceBiomatchComparing: distance =>
          set(state => {
            state.biomatchResultData.distance = distance;
          }),
        setImageDetails: ({type, ...imageDetails}) =>
          set(state => {
            state.imagesDetails[type] = {
              ...state.imagesDetails[type],
              ...imageDetails,
              uploaded: Boolean(imageDetails.image),
            };
          }),
        resetIVConfigState: () =>
          set(state => {
            state.biomatchResultData = initialState.biomatchResultData;
            state.imagesDetails = initialState.imagesDetails;
            state.ocrResultData = initialState.ocrResultData;
          }),
        resetIVSetupFormDate: () =>
          set(state => {
            state.setupData = initialState.setupData;
          }),
        resetAllIVState: () => set(() => initialState, false),
        initIVState: () =>
          set(
            state => ({
              ...initialState,
              isPropertyQRScanned: state.isPropertyQRScanned,
            }),
            false,
          ),
        sync: identityVerificationSchema => set(() => identityVerificationSchema),
        setRemoteSession: flag =>
          set(state => {
            state.isRemoteSession = flag;
          }),
        setLivenessResultData: livenessResultData =>
          set(state => {
            state.livenessResultData = {
              ...state.livenessResultData,
              ...livenessResultData,
            };
          }),
        setIsIvFailed: isIvFailed =>
          set(state => {
            state.isIvFailed = isIvFailed;
          }),
        setPropertyQRScanned: isPropertyQRScanned =>
          set(state => {
            state.isPropertyQRScanned = isPropertyQRScanned;
          }),
      })),
      {
        name: 'iv-qr-scanned',
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
  ),
);
